import { Component, computed, input } from '@angular/core';
import { LucideDynamicIcon } from '@lucide/angular';
import { NgpTabButton } from 'ng-primitives/tabs';
import { TabComponent } from './tab.component';

export type TabButtonSize = 'sm' | 'md' | 'lg';

@Component({
  selector: 'joii-tab-button',
  imports: [NgpTabButton, LucideDynamicIcon],
  template: `
    <button
      [ngpTabButtonValue]="tab()?.value()"
      [ngpTabButtonDisabled]="tab()?.disabled()"
      ngpTabButton
      [attr.data-size]="size()"
      [attr.data-automationid]="tab()?.automationId()"
    >
      <ng-content select="[slot=prefix]" />
      @if (tab()?.prefixIcon(); as prefixIcon) {
        <svg [lucideIcon]="prefixIcon" [size]="iconSize()" />
      }
      <span>{{ tab()?.label() }}</span>
      @if (tab()?.suffixIcon(); as suffixIcon) {
        <svg [lucideIcon]="suffixIcon" [size]="iconSize()" />
      }
      <ng-content select="[slot=suffix]" />
    </button>
  `,
  styles: `
    [ngpTabButton] {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: var(--Vetai-Spacing-2);
      border: none;
      background-color: transparent;
      color: var(--Vetai-Form_controls--Text_Default);
      font-style: normal;
      font-weight: var(--Vetai-Font-Weight-Regular);
      border-bottom: 2px solid transparent;
      padding: var(--Vetai-Spacing-2) 0;
      outline: none;
      transition: background-color 150ms cubic-bezier(0.4, 0, 0.2, 1);
    }

    [ngpTabButton] span {
      font-style: normal;
      font-weight: var(--Vetai-Font-Weight-Regular);
    }

    [ngpTabButton][data-hover] {
      color: var(--Vetai-Form_controls--Text_Hover);
      border-color: var(--Vetai-Form_controls--Text_Hover);
    }

    [ngpTabButton][data-focus-visible] {
      color: var(--Vetai-Form_controls--Text_Hover);
      border-color: var(--Vetai-Form_controls--Text_Hover);
    }

    [ngpTabButton][data-active] {
      border-color: var(--Vetai-Inline_links_and_info--Default);
      color: var(--Vetai-Inline_links_and_info--Default);
    }

    [ngpTabButton][data-disabled] {
      color: var(--Vetai-Form_controls--Placeholder);
      border-color: transparent;
      cursor: not-allowed;
    }

    [ngpTabButton][data-disabled][data-hover],
    [ngpTabButton][data-disabled][data-focus-visible],
    [ngpTabButton][data-disabled][data-active] {
      color: var(--Vetai-Form_controls--Placeholder);
      border-color: transparent;
    }

    [ngpTabButton][data-size='sm'] {
      padding: var(--Vetai-Spacing-1-5) var(--Vetai-Spacing-2);
    }

    [ngpTabButton][data-size='sm'] span {
      font-size: var(--Vetai-T4, 14px);
      line-height: var(--Vetai-20, 20px);
      letter-spacing: var(--Vetai-8-16, 0.35px);
    }

    [ngpTabButton][data-size='md'],
    [ngpTabButton]:not([data-size]) {
      padding: var(--Vetai-Spacing-2) var(--Vetai-Spacing-2-5);
    }

    [ngpTabButton][data-size='md'] span,
    [ngpTabButton]:not([data-size]) span {
      font-size: var(--Vetai-T5, 16px);
      line-height: var(--Vetai-24, 24px);
      letter-spacing: var(--Vetai-8-16, 0.35px);
    }

    [ngpTabButton][data-size='lg'] {
      padding: var(--Vetai-Spacing-2-5) var(--Vetai-Spacing-3);
    }

    [ngpTabButton][data-size='lg'] span {
      font-size: var(--Vetai-T6, 18px);
      line-height: var(--Vetai-28, 28px);
      letter-spacing: var(--Vetai-18-24, 0.25px);
    }
  `,
})
export class TabButtonComponent {
  readonly tab = input<TabComponent>();
  readonly size = input<TabButtonSize>('md');

  iconSize = computed(() => {
    const size = this.size();
    const sizeMap = {
      sm: 16,
      md: 20,
      lg: 24,
    };
    return sizeMap[size];
  });
}
